import PropTypes from 'prop-types';
import { Component } from 'react';

import styles from './IndexDocumentDisplay.scss';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import IndexDocumentHeader from 'indexDocuments/components/IndexDocumentHeader/IndexDocumentHeader';
import IndexDocumentPreviewContainer from 'indexDocuments/components/IndexDocumentPreviewContainer/IndexDocumentPreviewContainer';
import IndexDocumentDownloadOptions from 'indexDocuments/components/IndexDocumentDownloadOptions/IndexDocumentDownloadOptions';
import IndexDocumentDataEntry from 'indexDocuments/components/IndexDocumentDataEntry/IndexDocumentDataEntry';
import AscertainmentModal from 'indexDocuments/components/AscertainmentModal/AscertainmentModal';
import descriptorPropType from 'indexDocuments/util/descriptorPropType';

const validIndexingTypes = ['initialIndex', 'index', 'reindex'];

const UnrecognizedIndexingTypeComponent = () => (
    <div className={styles.container} data-cy="UnrecognizedIndexingTypeComponent">
        <div className={styles.centerDiv}>
            <p>Unable to recognize the indexing type.</p>
        </div>
    </div>
);

const LoadingIndexingDocumentDisplayComponent = () => (
    <div className={styles.container} data-cy="LoadingIndexingDocumentDisplayComponent">
        <div className={styles.centerDiv}>
            <LoadingComponent/>
        </div>
    </div>
);

const DocumentFailedToLoadComponent = ({ handleReload }) => (
    <div className={styles.container} data-cy="DocumentFailedToLoadComponent">
        <div className={styles.centerDiv}>
            <p><a href="#" onClick={handleReload}>The document failed to load. Click here to retry</a></p>
        </div>
    </div>
);

class IndexDocumentDisplay extends Component {
    render() {
        const {
            loading,
            loaded,
            failedToLoad,
            reload,
            binder,
            document,
            documentTypes,
            selectedDocumentType,
            descriptors,
            autonameValue,
            indexingType,
            documentTypesVisibility,
            documentTypeFilter,
            documentDownloadRequest,
        } = this.props;

        const indexingTypeIsInvalid = !validIndexingTypes.includes(indexingType);
        if (indexingTypeIsInvalid){
            return <UnrecognizedIndexingTypeComponent />;
        }

        if(loading){
            return <LoadingIndexingDocumentDisplayComponent />;
        }

        const documentFailedToLoad = failedToLoad || !loaded;
        if(documentFailedToLoad){
            const handleReload = e => {
                e.stopPropagation();
                e.preventDefault();
                reload();
            };
            return <DocumentFailedToLoadComponent handleReload={handleReload} />;
        }

        const { conversionStatus } = document;
        const shouldDisplayDownloadOptions = conversionStatus !== 'success';

        const IndexDocumentDownloadOptionsComponent = shouldDisplayDownloadOptions
            ? () => <IndexDocumentDownloadOptions document={document} documentDownloadRequest={documentDownloadRequest}/>
            : () => null;

        return (
            <div className={styles.container} data-cy="IndexDocumentDisplay">
                <AscertainmentModal/>
                <div className={styles.indexDocumentHeader} data-cy="IndexDocumentDisplay-Header">
                    <IndexDocumentHeader
                        document={document}
                        binder={binder}
                        indexingType={indexingType}
                        autonameValue={autonameValue}/>
                </div>
                <div className={styles.indexContainer} data-cy="IndexDocumentDisplay-indexContainer">
                    <div className={styles.dataEntryContainer}>
                        <IndexDocumentDataEntry
                            binder={binder}
                            indexingType={indexingType}
                            documentTypes={documentTypes}
                            selectedDocumentType={selectedDocumentType}
                            descriptors={descriptors}
                            documentTypesVisibility={documentTypesVisibility}
                            documentTypeFilter={documentTypeFilter}
                            isConversionSuccessful={!shouldDisplayDownloadOptions}/>
                    </div>
                    <div className={styles.previewContainer} data-cy="IndexDocumentDisplay-previewContainer">
                        <IndexDocumentPreviewContainer
                            document={document}/>
                        <IndexDocumentDownloadOptionsComponent/>
                    </div>
                </div>
            </div>
        );
    }
}

IndexDocumentDisplay.propTypes = {
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    failedToLoad: PropTypes.bool.isRequired,
    binder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
    }),
    document: PropTypes.shape({
        name: PropTypes.string.isRequired,
        uploadUser: PropTypes.shape({
            displayName: PropTypes.string.isRequired,
            emailAddress: PropTypes.string.isRequired,
        }).isRequired,
    }),
    documentTypesVisibility: PropTypes.bool.isRequired,
    documentTypes: PropTypes.objectOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
    })),
    selectedDocumentType: PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
    }),
    descriptors: PropTypes.objectOf(descriptorPropType),
    autonameValue: PropTypes.string,
    indexingType: PropTypes.string.isRequired,
};

export default IndexDocumentDisplay;
